import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Check, ShieldCheck, Zap, ArrowRight, Receipt, Clock, AlertTriangle, Copy, CreditCard, Smartphone } from "lucide-react";

const FEATURES = [
  "Unlimited clients & team members",
  "Vyapar-style GST invoicing + PDF",
  "Tax return tracker (GST · ITR · TDS · ROC)",
  "AI Assistant powered by GPT-5.2",
  "AI HSN/SAC finder + smart reminders",
  "WhatsApp Connect (Click-to-Chat)",
  "Document vault per client (50 MB/file)",
  "Bulk client import (Excel/CSV)",
  "Multi-bank, expenses, cash & books",
  "Granular role-based access",
];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Billing({ forced = false }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [c, s, p] = await Promise.all([
      api.get("/subscription/config"),
      api.get("/subscription/me"),
      api.get("/subscription/payments").catch(() => ({ data: [] })),
    ]);
    setConfig(c.data);
    setStatus(s.data);
    setPayments(p.data || []);
  };
  useEffect(() => { load(); }, []);

  const startCheckout = async (planId) => {
    if (!window.Razorpay) {
      toast.error("Payment script not loaded. Please refresh.");
      return;
    }
    setBusy(planId);
    try {
      const { data: order } = await api.post("/subscription/create-order", { plan: planId });
      const isTestKey = (order.razorpay_key_id || "").startsWith("rzp_test_");
      const options = {
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: order.firm_name || "TAXINPHO",
        description: `${planId === "monthly" ? "Monthly" : "Yearly"} subscription${isTestKey ? " (TEST mode)" : ""}`,
        theme: { color: "#002FA7" },
        prefill: order.prefill,
        notes: { plan: planId },
        // In test mode disable UPI QR (it cannot be scanned by real apps) and steer users to
        // the test card / test VPA flow instead. In live mode keep UPI QR available.
        config: isTestKey ? {
          display: {
            blocks: {
              cards: { name: "Pay with Card (test 4111 1111 1111 1111)", instruments: [{ method: "card" }] },
              upi_intent: { name: "Pay via UPI ID (test: success@razorpay)", instruments: [{ method: "upi", flows: ["collect", "intent"] }] },
              banks: { name: "NetBanking (any test bank)", instruments: [{ method: "netbanking" }] },
            },
            sequence: ["block.cards", "block.upi_intent", "block.banks"],
            preferences: { show_default_blocks: false },
          },
        } : undefined,
        handler: async (response) => {
          try {
            const { data } = await api.post("/subscription/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planId,
            });
            const updated = { ...(user || {}), ...(data.user || {}), subscription_status: data.subscription_status };
            localStorage.setItem("taxinpho_user", JSON.stringify(updated));
            setUser(updated);
            toast.success("Payment successful — subscription activated");
            await load();
            if (forced) navigate("/dashboard");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => setBusy(null),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Try again"}`);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  if (!config || !status) {
    return <div className="p-8 font-mono text-sm text-zinc-500">[ loading billing... ]</div>;
  }

  const isFounding = status.founding;
  const isPaid = status.status === "active" && !status.is_trial;
  const isTrial = status.is_trial;
  const isTestMode = (config.razorpay_key_id || "").startsWith("rzp_test_");

  const copyText = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success(`Copied: ${txt}`);
  };

  return (
    <div className="p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="billing-page">
      {forced && (
        <div className="mb-6 bg-[#FEF3F2] border-2 border-[#D92D20] p-5">
          <div className="flex items-start gap-3">
            <Clock size={20} className="text-[#D92D20] mt-1" />
            <div>
              <p className="label-caps text-[#D92D20]">Trial expired</p>
              <h3 className="font-display font-bold text-2xl tracking-tight">
                Your {config.trial_days}-day free trial has ended.
              </h3>
              <p className="text-sm text-zinc-700 mt-1">
                Subscribe below to keep using TAXINPHO. Your data is safe — nothing is deleted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TEST MODE notice — only shown to non-founding users when test keys are active */}
      {isTestMode && !isFounding && (
        <div className="mb-6 bg-[#FFFAEB] border-2 border-[#F79009] p-5" data-testid="test-mode-notice">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-[#B54708] mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="label-caps text-[#B54708] mb-1">Razorpay in TEST mode · No real money</p>
              <h3 className="font-display font-bold text-xl tracking-tight text-zinc-900 mb-2">
                Real UPI apps cannot scan this QR.
              </h3>
              <p className="text-sm text-zinc-700 leading-relaxed mb-4">
                The QR code shown in checkout is a <strong>test-only QR</strong>. Scanning it with GPay,
                PhonePe, Paytm or any UPI app will fail with &quot;Unable to verify QR code&quot;.
                To complete a test payment, use one of the methods below instead:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {/* Test card */}
                <div className="bg-white border border-zinc-300 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard size={16} className="text-[#002FA7]" />
                    <p className="label-caps text-[10px]">Option 1 · Test card</p>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Card number:</span>
                      <button onClick={() => copyText("4111111111111111")} data-testid="copy-test-card" className="font-mono font-semibold text-zinc-900 hover:text-[#002FA7] flex items-center gap-1">
                        4111 1111 1111 1111 <Copy size={11} />
                      </button>
                    </div>
                    <div className="flex justify-between"><span className="text-zinc-500">Expiry:</span><span className="font-mono">12 / 28</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">CVV:</span><span className="font-mono">123</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">Name:</span><span>Any name</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">OTP page:</span><span>Click &quot;Success&quot;</span></div>
                  </div>
                </div>

                {/* Test UPI */}
                <div className="bg-white border border-zinc-300 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone size={16} className="text-[#039855]" />
                    <p className="label-caps text-[10px]">Option 2 · Test UPI ID</p>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <p className="text-zinc-600 mb-2">
                      Pick <strong>UPI</strong> tab → choose <strong>&quot;Other&quot;</strong> or
                      <strong> Enter UPI ID</strong> (NOT the QR), and type:
                    </p>
                    <button onClick={() => copyText("success@razorpay")} data-testid="copy-test-upi" className="font-mono font-semibold text-zinc-900 hover:text-[#039855] flex items-center gap-1 bg-[#ECFDF3] px-2 py-1">
                      success@razorpay <Copy size={11} />
                    </button>
                    <p className="text-zinc-500 mt-1">(use <span className="font-mono">failure@razorpay</span> to simulate failure)</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#EFF4FF] border border-[#002FA7] p-3 text-xs text-zinc-700 leading-relaxed">
                <strong className="text-[#002FA7]">Ready to accept real payments?</strong> Complete KYC on your{" "}
                <a href="https://dashboard.razorpay.com/app/website-app-settings/api-keys" target="_blank" rel="noreferrer" className="underline font-semibold">Razorpay Dashboard</a>
                {" "}to activate live mode, then share new <code className="bg-zinc-100 px-1 font-mono">rzp_live_*</code> keys to swap into the app. Real UPI QR will work then.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status banner */}
      <div className="mb-8">
        <p className="label-caps mb-2">Billing</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-900">
          {isFounding ? "Founding member" : isPaid ? "You're subscribed" : isTrial ? "Free trial active" : "Choose a plan"}
        </h1>
        <p className="text-zinc-600 mt-2">
          {isFounding && "Lifetime free access — thanks for being an early supporter."}
          {!isFounding && isPaid && `Plan: ${status.plan} · ${status.days_left} day${status.days_left !== 1 ? "s" : ""} remaining.`}
          {!isFounding && isTrial && `${status.days_left} day${status.days_left !== 1 ? "s" : ""} left on your trial. Upgrade anytime.`}
          {!isFounding && status.status === "expired" && "Your access has expired. Subscribe to restore."}
        </p>
      </div>

      {/* Plans */}
      {!isFounding && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {config.plans.map((p) => {
            const isCurrent = isPaid && status.plan === p.id;
            const isYearly = p.id === "yearly";
            return (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`border-2 p-7 relative ${isYearly ? "border-[#002FA7] bg-[#EFF4FF]" : "border-zinc-300 bg-white"}`}
              >
                {isYearly && (
                  <span className="absolute -top-3 left-6 bg-[#002FA7] text-white text-xs font-semibold px-3 py-1">
                    BEST VALUE · Save {p.savings_pct}%
                  </span>
                )}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-bold text-2xl tracking-tight">{p.name}</h3>
                  {isCurrent && (
                    <span className="bg-[#039855] text-white text-xs font-semibold px-2 py-1">
                      ACTIVE
                    </span>
                  )}
                </div>
                <div className="mb-5">
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-black text-5xl tracking-tighter">
                      {inr(p.price_inr)}
                    </span>
                    <span className="text-zinc-600 text-sm">/ {p.interval}</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">{p.tagline}</p>
                </div>
                <ul className="space-y-2 mb-6">
                  {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={14} className="text-[#039855] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startCheckout(p.id)}
                  disabled={busy === p.id || isCurrent}
                  data-testid={`subscribe-${p.id}`}
                  className={`w-full py-3 font-semibold flex items-center justify-center gap-2 transition-all ${
                    isYearly
                      ? "bg-[#002FA7] text-white hover:bg-blue-800"
                      : "bg-zinc-900 text-white hover:bg-zinc-800"
                  } disabled:opacity-60`}
                >
                  {busy === p.id ? (
                    "Opening payment..."
                  ) : isCurrent ? (
                    <>Current plan</>
                  ) : (
                    <>
                      {isPaid ? "Switch to" : "Subscribe"} {p.name} <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Trust strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
        <div className="border border-zinc-200 p-4 flex items-start gap-3">
          <ShieldCheck size={18} className="text-[#002FA7] mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Secure payments</div>
            <div className="text-xs text-zinc-600">Razorpay · UPI · Cards · NetBanking</div>
          </div>
        </div>
        <div className="border border-zinc-200 p-4 flex items-start gap-3">
          <Sparkles size={18} className="text-[#002FA7] mt-0.5" />
          <div>
            <div className="font-semibold text-sm">GPT-5.2 included</div>
            <div className="text-xs text-zinc-600">AI for HSN, drafting, summaries, insights</div>
          </div>
        </div>
        <div className="border border-zinc-200 p-4 flex items-start gap-3">
          <Zap size={18} className="text-[#002FA7] mt-0.5" />
          <div>
            <div className="font-semibold text-sm">Cancel anytime</div>
            <div className="text-xs text-zinc-600">No lock-in. Refund within 7 days.</div>
          </div>
        </div>
      </div>

      {/* Payment history */}
      {payments.length > 0 && (
        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
            <Receipt size={16} />
            <h3 className="font-display font-bold text-base tracking-tight">Payment history</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-2 label-caps text-[10px]">Date</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Plan</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Amount</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Order ID</th>
                <th className="text-left px-4 py-2 label-caps text-[10px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100">
                  <td className="px-4 py-2 text-xs">
                    {new Date(p.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-2 capitalize">{p.plan}</td>
                  <td className="px-4 py-2 font-semibold">{inr(p.amount_inr)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-500">{p.id}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 ${p.status === "paid" ? "bg-[#ECFDF3] text-[#027A48]" : "bg-zinc-100 text-zinc-600"}`}>
                      {p.status.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
